import React, { useState, useMemo } from 'react';
import { useInventoryPlanning } from '../../contexts/InventoryPlanningContext';
import SelectSupplierLocationModal from '../SelectSupplierLocationModal';
import OutstockModal from '../OutstockModal';
import { useDatabaseContext } from '../../contexts/DatabaseContext';
import ArticleIdCell from '../ArticleIdCell';
import { Download } from 'lucide-react';
import { exportToCSV, formatArticleExport } from '../../utils/csvExport';
import { formatNumberWithColor } from '../../utils/numberFormatting';

const StockTodayTab: React.FC = () => {
  const { data, allowNegativeStock, selectedArticleIds } = useInventoryPlanning();
  const { activeDatabase } = useDatabaseContext();
  const [modalArticle, setModalArticle] = useState<string | null>(null);
  const [outstockOpen, setOutstockOpen] = useState(false);
  const [outstockArticleId, setOutstockArticleId] = useState<string | null>(null);
  
  const onhandRows = useMemo(() => (data?.onhand_today || []) as any[], [data?.onhand_today]);
  const articleMetas = useMemo(() => (data?.articles || []) as any[], [data?.articles]);
  const articleDetails = useMemo(() => (data as any)?.article_details as any[] | Record<string, any> | undefined, [data]);
  const detailsArray = useMemo(() => {
    if (Array.isArray(articleDetails)) {
      return articleDetails as any[];
    }
    if (articleDetails) {
      return Object.values(articleDetails as Record<string, any>);
    }
    return [];
  }, [articleDetails]);

  // Build a merged list of rows so every selected article appears even if on-hand is missing
  const mergedRows = React.useMemo(() => {
    const onhandMap: Record<string, number> = {};
    const baseDateMap: Record<string, string | undefined> = {};
    const onhandPresence = new Set<string>();
    for (const r of onhandRows) {
      const key = String(r?.sku_id);
      onhandPresence.add(key);
      onhandMap[key] = Number(r?.onhand_qty ?? 0);
      baseDateMap[key] = r?.base_stock_date ? String(r.base_stock_date) : undefined;
    }

    const allSkuIds = new Set<string>();
    // From articles meta (primary source of selected article ids)
    for (const a of articleMetas) allSkuIds.add(String(a?.article_id));
    // From article details if present (ref matches sku_id)
    for (const d of detailsArray) if (d && d.ref !== undefined) allSkuIds.add(String(d.ref));
    // Also include any sku that only appears in onhand (safety)
    for (const r of onhandRows) allSkuIds.add(String(r?.sku_id));

    return Array.from(allSkuIds).map((sku) => ({
      sku_id: sku,
      onhand_qty: onhandPresence.has(sku) ? onhandMap[sku] : undefined,
      base_stock_date: onhandPresence.has(sku) ? baseDateMap[sku] : undefined,
      __hasOnhand: onhandPresence.has(sku),
    }));
  }, [onhandRows, articleMetas, detailsArray]);

  const sortedRows = React.useMemo(() => {
    if (!mergedRows || mergedRows.length === 0) return [] as any[];
    return [...mergedRows].sort((a: any, b: any) =>
      String(a?.sku_id ?? '').localeCompare(String(b?.sku_id ?? ''), undefined, { numeric: true, sensitivity: 'base' })
    );
  }, [mergedRows]);

  const getArticleDetail = (skuId: string | number) => {
    if (!articleDetails) return undefined;
    // Match by 'ref' which corresponds to sku_id
    const ref = String(skuId);
    const found = detailsArray.find((d: any) => String(d?.ref) === ref);
    return found;
  };

  const dynamicKeys = React.useMemo(() => {
    if (!detailsArray || detailsArray.length === 0) return [] as string[];
    const allKeys = new Set<string>();
    detailsArray.forEach((d) => {
      Object.keys(d || {}).forEach((k) => allKeys.add(k));
    });
    // We already show sku_id (ref). Exclude 'ref'. Prefer 'nom' first if present.
    const keys = Array.from(allKeys).filter((k) => k !== 'ref');
    const hasNom = keys.includes('nom');
    const withoutNom = keys.filter((k) => k !== 'nom');
    // Keep a stable alphabetical order for the rest to avoid column thrashing
    withoutNom.sort();
    return hasNom ? ['nom', ...withoutNom] : withoutNom;
  }, [detailsArray]);

  // Filter articles and details by selectedArticleIds
  const selectedArticles = React.useMemo(() => {
    if (!selectedArticleIds || selectedArticleIds.length === 0) return [];
    return articleMetas.filter((a: any) => selectedArticleIds.includes(String(a?.article_id)));
  }, [articleMetas, selectedArticleIds]);

  const selectedArticleDetails = React.useMemo(() => {
    if (!selectedArticleIds || selectedArticleIds.length === 0) return [];
    return detailsArray.filter((d: any) => d && d.ref && selectedArticleIds.includes(String(d.ref)));
  }, [detailsArray, selectedArticleIds]);

  const selectedStockData = React.useMemo(() => {
    if (!selectedArticleIds || selectedArticleIds.length === 0) return [];
    const today = new Date().toISOString().slice(0, 10);
    return onhandRows
      .filter((r: any) => selectedArticleIds.includes(String(r?.sku_id)))
      .map((r: any) => ({
        article_id: String(r.sku_id),
        date: today,
        quantity: Math.floor(Number(r.onhand_qty || 0)),
      }));
  }, [onhandRows, selectedArticleIds]);

  const handleExportArticles = () => {
    const formatted = formatArticleExport(selectedArticles, selectedArticleDetails);
    exportToCSV(formatted, `articles_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const handleExportStock = () => {
    exportToCSV(selectedStockData, `stock_${new Date().toISOString().slice(0, 10)}.csv`, ['article_id', 'date', 'quantity']);
  };

  return (
    <div className="bg-white shadow rounded p-4" style={{ minWidth: '600px' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-medium">Stock actuel (aujourd'hui)</div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportArticles}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
            title="Exporter les articles sélectionnés"
          >
            <Download size={14} />
            Exporter Articles
          </button>
          <button
            onClick={handleExportStock}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors border border-green-200"
            title="Exporter le stock actuel"
          >
            <Download size={14} />
            Exporter Stock
          </button>
        </div>
      </div>
      <div className="text-xs text-gray-500 mb-2">{sortedRows.length} articles</div>
      <div className="overflow-auto border rounded">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-700">
              <th className="px-3 py-2 text-left">article</th>
              <th className="px-3 py-2 text-right">stock actuel</th>
              <th className="px-3 py-2 text-left">date d'import</th>
              <th className="px-3 py-2 text-center">rupture</th>
              {dynamicKeys.map((k) => (
                <th key={k} className="px-3 py-2 text-left">{k}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.slice(0, 200).map((r: any) => (
              <tr key={String(r.sku_id)} className="border-t">
                <td className="px-3 py-2">
                  <ArticleIdCell id={String(r.sku_id)} onClick={() => setModalArticle(String(r.sku_id))} />
                </td>
                <td className="px-3 py-2 text-right">
                  {r.onhand_qty === null || r.onhand_qty === undefined ? (
                    <span className="text-xs text-gray-500">—</span>
                  ) : (
                    formatNumberWithColor(allowNegativeStock ? r.onhand_qty : Math.max(0, Number(r.onhand_qty)), 2)
                  )}
                </td>
                <td className="px-3 py-2 text-left">
                  {r.base_stock_date ? (
                    String(r.base_stock_date).slice(0, 10)
                  ) : (
                    <span className="text-xs text-gray-500">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  <button
                    className="px-2 py-1 text-xs bg-red-600 text-white rounded"
                    onClick={() => { setOutstockArticleId(String(r.sku_id)); setOutstockOpen(true); }}
                  >
                    Déclarer
                  </button>
                </td>
                {dynamicKeys.map((k) => {
                  const detail = getArticleDetail(r.sku_id);
                  const value = detail ? detail[k] : undefined;
                  const formatted = (() => {
                    if (value === null || value === undefined || value === '') return null;
                    const num = Number(value);
                    return isFinite(num) && String(value).trim() !== '' && /^-?\d+(\.\d+)?$/.test(String(value))
                      ? num.toFixed(2)
                      : String(value);
                  })();
                  return (
                    <td key={k} className="px-3 py-2 text-left">
                      {formatted === null ? (
                        <span className="text-xs text-gray-500">—</span>
                      ) : (
                        formatted
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SelectSupplierLocationModal open={!!modalArticle} articleId={modalArticle} onClose={() => setModalArticle(null)} />
      {outstockOpen && activeDatabase?.id && outstockArticleId && (
        <OutstockModal
          open={outstockOpen}
          onClose={() => { setOutstockOpen(false); setOutstockArticleId(null); }}
          databaseId={String(activeDatabase.id)}
          article={{ id: outstockArticleId } as any}
        />
      )}
    </div>
  );
};

export default StockTodayTab;


